<script setup lang="ts">
import { ToastPortal, ToastProvider, ToastViewport } from 'reka-ui'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  APP_CORNER_BOTTOM_MARGIN_PX,
  GAP_TOAST_STACK_ABOVE_TOOLBOX_PX,
  sourceToastStackUnitPx,
  useSourceToast,
} from '~/composables/ui/useSourceToast'
import { useAppLayoutFooterFixedBottomLiftPx } from '~/composables/ui/useAppLayoutFooterFixedBottomLiftPx'
import { useSeasonalCornerBottomInsetPx } from '~/composables/ui/useSeasonalCornerBottomInsetPx'
import {
  ASYNC_OVERLAY_DELAY_MS,
  defineAsyncWithSkeleton,
} from '~/utils/defineAsyncWithSkeleton'
import { formatSourceToastStackHint } from '~/utils/sourceToastStackHint'

const SourceToastItem = defineAsyncWithSkeleton(
  function loadSourceToastItem() {
    return import('~/components/ui/SourceToastItem.vue')
  },
  undefined,
  ASYNC_OVERLAY_DELAY_MS,
)

defineOptions({ name: 'SourceToastProvider' })

const { toasts, defaultDurationMs, toastStackAnchorClientHeightPx, toolboxColumnReservePx } =
  useSourceToast()
const seasonalCornerBottomInsetPx = useSeasonalCornerBottomInsetPx()
const { extraBottomPx: appLayoutFooterExtraBottomPx } = useAppLayoutFooterFixedBottomLiftPx()

const TOAST_STACK_VIEWPORT_MARGIN_PX = 40

/** Conteneur qui scroll (borne `max-height`) - le <ol> Reka grandit avec le contenu. */
const stackAnchorRef = ref<HTMLElement | null>(null)

const viewportHeightPx = ref(0)

const scrollTopPx = ref(0)
const scrollHeightPx = ref(0)
const clientHeightPx = ref(0)

const stackUnitPx = sourceToastStackUnitPx()

const isToastUiMounted = ref(false)

let resizeObserver: ResizeObserver | null = null
let listResizeObserver: ResizeObserver | null = null

function resolveStackAnchorEl(): HTMLElement | null {
  if (stackAnchorRef.value instanceof HTMLElement) {
    return stackAnchorRef.value
  }
  if (import.meta.client) {
    const fallback = document.querySelector('.source-toast-stack-anchor')
    if (fallback instanceof HTMLElement) {
      return fallback
    }
  }
  return null
}

function updateScrollMetrics(): void {
  const element = resolveStackAnchorEl()
  if (!element) {
    toastStackAnchorClientHeightPx.value = 0
    return
  }
  scrollTopPx.value = element.scrollTop
  scrollHeightPx.value = element.scrollHeight
  clientHeightPx.value = element.clientHeight
  viewportHeightPx.value = element.clientHeight
  const hasVisibleToasts = toasts.value.length > 0
  toastStackAnchorClientHeightPx.value = hasVisibleToasts ? element.clientHeight : 0
}

function onStackAnchorScroll(): void {
  updateScrollMetrics()
}

function scrollStackToBottom(): void {
  const element = resolveStackAnchorEl()
  if (!element) {
    return
  }
  element.scrollTop = element.scrollHeight - element.clientHeight
  updateScrollMetrics()
}

/** Affiche le haut de la pile : le toast le plus ancien (index 0) pendant sa fermeture / sortie. */
function scrollStackToShowOldest(): void {
  const element = resolveStackAnchorEl()
  if (!element) {
    return
  }
  element.scrollTop = 0
  updateScrollMetrics()
}

function alignStackScroll(): void {
  const list = toasts.value
  const oldestIsClosing = list.length > 0 && list[0] != null && !list[0].open
  if (oldestIsClosing) {
    scrollStackToShowOldest()
  } else {
    scrollStackToBottom()
  }
}

const isScrollable = computed(function computeIsScrollable() {
  return scrollHeightPx.value > clientHeightPx.value + 1
})

const hiddenAboveApprox = computed(function computeHiddenAboveApprox() {
  if (!isScrollable.value) {
    return 0
  }
  return Math.max(0, Math.ceil(scrollTopPx.value / stackUnitPx))
})

const hiddenBelowApprox = computed(function computeHiddenBelowApprox() {
  if (!isScrollable.value) {
    return 0
  }
  const remainingScrollable =
    scrollHeightPx.value - clientHeightPx.value - scrollTopPx.value
  return Math.max(0, Math.ceil(remainingScrollable / stackUnitPx))
})

const showTopFade = computed(function computeShowTopFade() {
  return scrollTopPx.value > 4
})
const showBottomFade = computed(function computeShowBottomFade() {
  if (!isScrollable.value) {
    return false
  }
  return scrollTopPx.value < scrollHeightPx.value - clientHeightPx.value - 4
})

const stackHint = computed(function computeStackHint() {
  return formatSourceToastStackHint({
    isScrollable: isScrollable.value,
    hiddenAbove: hiddenAboveApprox.value,
    hiddenBelow: hiddenBelowApprox.value,
  })
})

function toastStackBottomPx(): number {
  const seasonalInset = seasonalCornerBottomInsetPx.value
  const footerLiftPx = appLayoutFooterExtraBottomPx.value
  const reserve = toolboxColumnReservePx.value
  const gapAboveToolbox = reserve > 0 ? GAP_TOAST_STACK_ABOVE_TOOLBOX_PX : 0
  return APP_CORNER_BOTTOM_MARGIN_PX + seasonalInset + footerLiftPx + reserve + gapAboveToolbox
}

const toastStackAnchorStyle = computed(function computeToastStackAnchorStyle() {
  const seasonalInset = seasonalCornerBottomInsetPx.value
  return {
    bottom: `${toastStackBottomPx()}px`,
    right: '20px',
    maxHeight: `min(50vh, calc(100vh - ${TOAST_STACK_VIEWPORT_MARGIN_PX + seasonalInset}px))`,
  }
})

const hintBottomStyle = computed(function computeHintBottomStyle() {
  return { bottom: `${toastStackBottomPx() + viewportHeightPx.value + 8}px` }
})

function bindStackAnchor(): void {
  const element = resolveStackAnchorEl()
  if (!element) {
    return
  }
  updateScrollMetrics()
  resizeObserver?.disconnect()
  resizeObserver = new ResizeObserver(updateScrollMetrics)
  resizeObserver.observe(element)

  listResizeObserver?.disconnect()
  const listRoot = element.querySelector('ol.source-toast-viewport')
  if (listRoot instanceof HTMLElement) {
    listResizeObserver = new ResizeObserver(updateScrollMetrics)
    listResizeObserver.observe(listRoot)
  }
}

function alignStackAfterPaint(): void {
  bindStackAnchor()
  alignStackScroll()
  const oldestToast = toasts.value[0]
  if (!oldestToast || oldestToast.open) {
    return
  }
  requestAnimationFrame(scrollStackToShowOldest)
}

function scheduleStackScrollAlign(): void {
  void nextTick(alignStackAfterPaint)
}

function bindAndAlignStack(): void {
  bindStackAnchor()
  alignStackScroll()
}

function refreshScrollMetricsAfterLayout(): void {
  void nextTick(updateScrollMetrics)
}

watch(toasts, scheduleStackScrollAlign, { deep: true })
watch(seasonalCornerBottomInsetPx, refreshScrollMetricsAfterLayout)
watch(stackAnchorRef, function onStackAnchorRefChanged() {
  void nextTick(bindAndAlignStack)
})

onMounted(function onToastProviderMounted() {
  isToastUiMounted.value = true
  void nextTick(bindAndAlignStack)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
  listResizeObserver?.disconnect()
  listResizeObserver = null
  toastStackAnchorClientHeightPx.value = 0
})
</script>

<template>
  <!-- Racine unique : évite un fragment slot + ClientOnly (hydratation SSR / Reka UI). -->
  <div class="source-toast-provider-root">
    <slot />
    <ToastProvider
      v-if="isToastUiMounted"
      :duration="defaultDurationMs"
      label="Notification"
      swipe-direction="right"
    >
      <ToastPortal>
        <div
          ref="stackAnchorRef"
          class="source-toast-stack-anchor"
          :style="toastStackAnchorStyle"
          :class="{
            'source-toast-stack-anchor--scrollable': isScrollable,
            'source-toast-stack-anchor--fade-top': showTopFade,
            'source-toast-stack-anchor--fade-bottom': showBottomFade,
          }"
          @scroll.passive="onStackAnchorScroll"
        >
          <ToastViewport
            class="source-toast-viewport"
            label="Notifications ({hotkey})"
          />
        </div>
      </ToastPortal>
      <p
        v-if="stackHint"
        class="source-toast-stack-hint"
        role="status"
        :style="hintBottomStyle"
      >
        {{ stackHint }}
      </p>
      <SourceToastItem
        v-for="(entry, queueIndex) in toasts"
        :key="entry.id"
        :item="entry"
        :queue-index="queueIndex"
      />
    </ToastProvider>
  </div>
</template>

<style lang="scss">
@use '~/assets/scss/partials/elpdl/colors' as c;
@use '~/assets/scss/partials/elpdl/spacing' as *;

.source-toast-provider-root {
  display: contents;
}

/* Scroll + max-height sur l’ancre : le <ol> reste en hauteur intrinsèque ; flex-end sur le <ol> seul cassait overflow. */
.source-toast-stack-anchor {
  position: fixed;
  /* bottom / right / max-height : dynamiques via toastStackAnchorStyle (déco événement saisonnier) */
  z-index: 12500; /* au-dessus de la toolbox (12000) */
  width: min(400px, calc(100vw - 40px));
  box-sizing: border-box;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.source-toast-stack-anchor--scrollable.source-toast-stack-anchor--fade-top {
  box-shadow: inset 0 20px 20px -18px rgba(16, 16, 20, 0.16);
}

.source-toast-stack-anchor--scrollable.source-toast-stack-anchor--fade-bottom {
  box-shadow: inset 0 -20px 20px -18px rgba(16, 16, 20, 0.16);
}

.source-toast-stack-anchor--scrollable.source-toast-stack-anchor--fade-top.source-toast-stack-anchor--fade-bottom {
  box-shadow:
    inset 0 20px 20px -18px rgba(16, 16, 20, 0.16),
    inset 0 -20px 20px -18px rgba(16, 16, 20, 0.16);
}

.source-toast-viewport {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: $spacing-xs;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
  min-width: 0;
  outline: none;
  box-sizing: border-box;
}

.source-toast-stack-hint {
  position: fixed;
  right: $spacing-l;
  z-index: 12501;
  margin: 0;
  max-width: min(400px, calc(100vw - 2 * #{$spacing-3xl}));
  padding: $spacing-2xs $spacing-xs;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  text-align: center;
  color: c.$enedis-neutral-800;
  background: c.$enedis-neutral-20;
  border: 1px solid c.$enedis-neutral-40;
  box-shadow: 0 2px $spacing-2xs rgba(16, 16, 20, 0.16);
  pointer-events: none;
  box-sizing: border-box;
}
</style>
